import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { ChangeEvent } from 'react';
import {
    CheckboxElement,
    FormContainer,
    TextFieldElement,
    useForm,
    useWatch,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import {
    BusinessInput,
    PourTypeInput,
    PriceTierInput,
    PriceTierScopeInput,
    ProductSubtypeInput,
    ProductTypeInput,
} from '@barboxx/admin-components';
import {
    CREATE_BUSINESS_PRICE_TIER_SCHEMA,
    useBusinessSelector,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { BusinessPriceTier } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateBusinessPriceTierDialog = ({
    onClose,
    onSuccess,
    open,
}: Props) => {
    const notifications = useNotifications();
    const { businessId } = useBusinessSelector();

    const formContext = useForm<BusinessPriceTier.CreateRequest>({
        resolver: yupResolver(CREATE_BUSINESS_PRICE_TIER_SCHEMA),
        defaultValues: {
            businessId,
            isDefault: false,
            productTypeId: null,
            productSubtypeId: null,
        },
    });

    const mutateCreateBusinessPriceTier = useMutation({
        mutationKey: ['businessPriceTier'],
        mutationFn: (data: BusinessPriceTier.CreateRequest) =>
            Api.BusinessSpecific.PriceTier.postPriceTier(data),
    });

    const onSubmit = async (values: BusinessPriceTier.CreateRequest) => {
        return mutateCreateBusinessPriceTier
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating business price tier!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    const handleDefaultChange = (
        e: ChangeEvent<HTMLInputElement>,
        checked: boolean
    ) => {
        if (checked) {
            formContext.setValue('defaultUpCharge', 0);
        }
    };

    const isDefault = useWatch({
        control: formContext.control,
        name: 'isDefault',
    });

    const tierScopeValue = useWatch({
        control: formContext.control,
        name: 'tierScope' as never,
    }) as 'type' | 'subtype' | undefined;

    const productSubtypeId = useWatch({
        control: formContext.control,
        name: 'productSubtypeId',
    });

    const tierScope = tierScopeValue ?? (productSubtypeId ? 'subtype' : 'type');

    return (
        <Dialog
            open={open}
            onClose={handleCancelCreate}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Business Price Tier
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <BusinessInput />
                    <PriceTierInput />
                    <PriceTierScopeInput />
                    {tierScope === 'type' ? (
                        <ProductTypeInput />
                    ) : (
                        <ProductSubtypeInput />
                    )}
                    <PourTypeInput />
                    {!isDefault && (
                        <TextFieldElement
                            label="Default Up Charge"
                            name="defaultUpCharge"
                            type="number"
                            fullWidth
                            slotProps={{
                                htmlInput: {
                                    max: 1000,
                                    min: 0,
                                },
                            }}
                        />
                    )}
                    <TextFieldElement
                        label="Display Order"
                        name="displayOrder"
                        type="number"
                        fullWidth
                        slotProps={{
                            htmlInput: {
                                max: 1000,
                                min: 0,
                                step: 1,
                            },
                        }}
                    />
                    <CheckboxElement
                        label="Default"
                        name="isDefault"
                        onChange={handleDefaultChange}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
